import React, { useCallback, useMemo, useState } from 'react';

import { constants, DateService, IntlManager, NotificationManager } from '@atlas/core';
import { Button, Icon, Label } from '@atlas/ui';

import { transactionStatuses } from 'common/constants';
import ConfirmPopup from 'common/Popup/ConfirmPopup';
import { getReadableString } from 'common/utils/utils';
import TransactionCancelButton from 'components/ClientProfile/Bonuses/BonusesHistory/TransactionCancelButton';

const { COMPLETED, FAILED, PROCESSING, SUCCEED } = transactionStatuses;

const BonusesHistoryTableItem = ({ currentPage, transaction, updateTransactionsAfterCancel }) => {
  const { amount, comment, creationDate, description, id, retail, status, type } = transaction;

  const [showDetailCancel, setShowDetailCancel] = useState(false);
  const handleDetailCancel = useCallback(() => {
    setShowDetailCancel(showDetailCancel => !showDetailCancel);
  }, [setShowDetailCancel]);

  const sign = ['DEPOSIT', 'CORRECTION_BALANCE_DEPOSIT'].includes(type) ? '+' : '-';

  const formattedDate = DateService.getFormattedDateWithTime(creationDate);

  const statusBlock = useMemo(() => {
    if (status === SUCCEED) {
      return (
        <span className="transaction-status-label">
          <span className="sub-title-xsmall-300-text">
            <Label message="Status" />:
          </span>
          <Label className="sub-title-small-300-text" message={status.toLowerCase()} />
        </span>
      );
    }
    if (status === COMPLETED) {
      const label = 'transaction_successful';
      return (
        <span className="transaction-status-label">
          <span className="sub-title-xsmall-300-text">
            <Label message="Status" />:
          </span>
          <Label className="sub-title-small-300-text" message={label.toLowerCase()} />
        </span>
      );
    }
    if (status === FAILED) {
      return (
        <>
          <span className="transaction-status-label">
            <Icon className="icon-warning" name="warning" />
            <span className="sub-title-xsmall-300-text">
              <Label message="Status" />:
            </span>
            <span className="sub-title-small-300-text">
              <Label message={status.toLowerCase()} />.
              <div className="link-small-text" onClick={handleDetailCancel}>
                <Label message="read_more" />
              </div>
            </span>
          </span>
        </>
      );
    }
    if (status === PROCESSING) {
      return (
        <span className="transaction-status-label">
          <Icon className="icon-in-procces" name="in-procces" />
          <span className="sub-title-xsmall-300-text">
            <Label message="Status" />:
          </span>
          <Label className="sub-title-small-300-text" message={status.toLowerCase()} />
          <span className="link-small-text">
            {type === constants.PAYMENT_OPERATION.WITHDRAWAL && (
              <TransactionCancelButton
                currentPage={currentPage}
                transactionId={id}
                updateTransactionsAfterCancel={updateTransactionsAfterCancel}
              />
            )}
          </span>
        </span>
      );
    }
  }, [status, type]);

  const showWithdrawalCode = useCallback(() => {
    NotificationManager.display({
      data: { id },
      message: 'withdrawalCodePopup',
      type: 'withdrawalCodePopup',
    });
  }, [id]);

  return (
    <>
      <div className="transaction-history-list-item">
        <div>
          <div className="col">
            {type === 'WITHDRAWAL' && retail && (
              <Button
                className="withdrawal-code__button"
                onClick={showWithdrawalCode}
                type="secondary"
              >
                <Label message="withdrawal_code" />
              </Button>
            )}
            <div className="sub-title-xsmall-300-text flex-element">
              <span className="transaction-type">
                <Label message={getReadableString(type)} />:
              </span>
              <span className="transaction-id">#{id}</span>
            </div>
            <div className="sub-title-xsmall-300-text flex-element">
              <span>{formattedDate}</span>
            </div>
            <div className="sub-title-xsmall-300-text">
              <span>{description}</span>
            </div>
            <div className="transaction-status-block">{statusBlock}</div>
          </div>
          <div className="col">
            <span className="transaction-amount-label">
              {sign}
              {IntlManager.formatMoney(amount)}
            </span>
          </div>
        </div>
      </div>
      {showDetailCancel && (
        <ConfirmPopup
          data={{
            className: 'popup__transaction-cancel',
            onConfirm: handleDetailCancel,
            secondaryBtn: false,
          }}
          title="transaction_declined_message"
        >
          <div className="popup__text">{comment}</div>
        </ConfirmPopup>
      )}
    </>
  );
};

export default BonusesHistoryTableItem;
